import {
    fetchAllProjects,
    fetchDeveloperProjects,
    registerProject,
    unregisterProject,
    addMembers
} from "@/api";
import router from "@/router";
import i18n from "@/i18n";
import { DEVELOPER_PROJECTS, DEVELOPER_PROJECT_DETAIL } from "@/router/path";

export const state = {
    projects: [],
    loadingMyProjects: false,
    myProjects: [],
    searchKeyword: "",
    projectCodeInput: ""
};

export const getters = {
    projects: ({ projects }) => projects,
    myProjects: ({ myProjects }) => myProjects,
    myProjectIds: ({ myProjects }) => myProjects.map(project => project.id),
    hasProject: ({ myProjects }) => !!myProjects.length,
    approvedProjects: ({ myProjects }, getters, rootState, { userEmail }) =>
        myProjects.filter(item => {
            const { status } = item.members.find(
                ({ email }) => email === userEmail
            );
            return status !== "WAIT_APPROVAL";
        }),
    hasApprovedProject: (state, { approvedProjects }) =>
        approvedProjects.length !== 0,
    searchKeyword: ({ searchKeyword }) => searchKeyword,
    projectTableRow: ({ myProjects }, getters, rootState, { userEmail }) =>
        myProjects.map(item => {
            const { role, status } =
                item.members.find(({ email }) => email === userEmail) || {};
            const isWaiting = status === "WAIT_APPROVAL";
            const row = ["name", "appCount", "status"].map(key => {
                const value = item[key];
                const cell = { text: value || "-" };
                switch (key) {
                case "appCount":
                    cell.text = isWaiting
                        ? "-"
                        : i18n.t("projects.appCount", [value]);
                    break;
                case "status":
                    cell.text =
                            i18n.t("projects.status")[
                                isWaiting ? status : role
                            ];
                    break;
                }
                return cell;
            });
            row.id = item.id;
            return row;
        }),
    projectByName:
        ({ projects }) =>
            name =>
                projects.find(project => project.name === name)
};

export const actions = {
    async FETCH_PROJECTS({ commit }) {
        try {
            const { data } = await fetchAllProjects();
            commit("SET_PROJECTS", data);
        } catch (e) {
            //
        }
    },
    async FETCH_MY_PROJECTS({ commit, getters }) {
        try {
            commit("SET_LOADING_MY_PROJECTS", true);
            const { data } = await fetchDeveloperProjects(getters.userId);
            await commit("SET_MY_PROJECTS", data);
        } catch (e) {
            //
        } finally {
            commit("SET_LOADING_MY_PROJECTS", false);
        }
    },
    async REGISTER_DEVELOPER_PROJECT({
        state: { searchKeyword: name, projectCodeInput: code },
        getters: { projectByName }
    }) {
        const existedProject = projectByName(name);
        if (existedProject) throw new Error("DUPLICATED_LABEL_NAME");

        try {
            const resp = await registerProject({ name, code });
            const { data: { id: projectId } } = resp;
            router.replace(`${DEVELOPER_PROJECT_DETAIL}/${projectId}`);
        } catch (e) {
            const { code } = e?.response?.data || {};
            if (code) throw new Error(code);
        }
    },
    async JOIN_DEVELOPER_PROJECT({ getters }) {
        const { searchKeyword, projectByName, myProjects } = getters;
        const duplicated = myProjects.find(
            ({ name }) => name === searchKeyword
        );
        if (duplicated) throw new Error("DUPLICATED_LABEL_NAME");

        // TBD project 존재하지 않을 때 예외처리
        const { id: projectId } = projectByName(searchKeyword);
        try {
            const { userEmail: email } = getters;
            const response = await addMembers(projectId, { type: "APPROVAL" }, [{ email }]);
            const { data: { id } } = response;
            router.replace(`${DEVELOPER_PROJECT_DETAIL}/${id}`);
        } catch (e) {
            //
        }
    },
    async UNREGISTER_DEVELOPER_PROJECT({
        rootState: { route: { params: { pId: projectId } } },
        getters: { userId }
    }) {
        try {
            await unregisterProject(userId, projectId);
            router.replace(DEVELOPER_PROJECTS);
        } catch (e) {
            //
        }
    },
    CLICK_REGISTER_PROJECT({ dispatch }) {
        dispatch("UPDATE_DIALOG", {
            title: i18n.t("alertCreate.project.title"),
            description: i18n.t("alertCreate.project.content"),
            slot: () =>
                import("@/components/dialogs/project/Dialog.content.vue"),
            actions: [{ action: "confirm", label: i18n.t("label.confirm") }]
        });
    },
    CLICK_REGISTER_APP({ dispatch }) {
        dispatch("UPDATE_DIALOG", {
            title: i18n.t("alertCreate.application.title"),
            description: i18n.t("alertCreate.application.content"),
            slot: () =>
                import("@/components/dialogs/project/Dialog.content.vue"),
            actions: [{ action: "confirm", label: i18n.t("label.confirm") }]
        });
    },
    SEARCH_PROJECTS({ commit }, { name: keyword }) {
        if (!keyword) return;
        commit("SET_SEARCH_KEYWORD", keyword);
    },
    RESET_PROJECTS({ commit }) {
        commit("SET_PROJECTS", []);
        commit("SET_MY_PROJECTS", []);
        commit("SET_SEARCH_KEYWORD", "");
    }
};
export const mutations = {
    SET_PROJECTS(state, value) {
        state.projects = value;
    },
    SET_MY_PROJECTS(state, value) {
        state.myProjects = value;
    },
    SET_SEARCH_KEYWORD(state, value) {
        state.searchKeyword = value;
    },
    SET_LOADING_MY_PROJECTS(state, value) {
        state.loadingMyProjects = value;
    },
    SET_PROJECT_CODE_INPUT(state, value) {
        state.projectCodeInput = value;
    }
};
